import {Tr} from "@/i18n/i18n";
import {Player, UserSource} from "@/store/player/types";
import ListenerManager from "@/managers/listener-manager";
import {Http} from "@/constants/http";
import SecurityStore from "@/store/security";
import {BankOption} from "@/store/shop/types";
import CU from "@/utils/c-u";
import {GameState, YandexProduct} from "@/constants/game-state";
import PlayerStore from "@/store/player";
import ShopStore from "@/store/shop";

export interface AppleAuthResult {
    idToken: string;
    fullName: string;
    email: string;
}

const APPLE_CLIENT_ID = 'mobi.mirland.aof';

export class IntegrationUtils {

    private static mystoreRegisterCallback: (uid: string, hash: string) => unknown;

    public static isEnglishServer(): boolean {
        let host = window.location.hostname;
        return host.includes('mirland.mobi');
    }

    public static isLocalhost(): boolean {
        let host = window.location.hostname;
        return host.includes('localhost');
    }

    public static isRussianServer(): boolean {
        let host = window.location.hostname;
        return host.includes('zazer.mobi');
    }

    public static isOkUser(): boolean {
        let host = window.location.hostname;
        return host.includes('ok.zazer.mobi') || host.includes('otest.zazer');
    }

    public static isVkUser(): boolean {
        let host = window.location.hostname;
        return host.includes('vk.zazer.mobi');
    }

    public static isGPUser(): boolean {
        let host = window.location.hostname;
        return host.includes('gp.release.lgtest.fun') || host.includes('gp.dev.lgtest.fun') || host.includes('gp.feature.lgtest.fun') || host.includes('gp.zazer.mobi');
    }

    public static isExeUser(): boolean {
        let host = window.location.hostname;
        return host.includes('exe.zazer.mobi') || host.includes('exe.lgtest.fun');
    }

    public static isBeelineUser(): boolean {
        let host = window.location.hostname;
        return host.includes('beeline.zazer.mobi') || host.includes('beeline.lgtest.fun');
    }

    public static isMystoreUser(): boolean {
        let host = window.location.hostname;
        return host.includes('mystore.zazer.mobi');
    }

    public static isYandexUser(): boolean {
        let host = window.location.hostname;
        return host.includes('ya.zazer.mobi');
    }

    public static isTelegramUser(): boolean {
        let host = window.location.hostname;
        return host.includes('tg.zazer.mobi') ||
          host.includes('tg.mirland.mobi') ||
          host.includes('tg.release.lgtest.fun') ||
          host.includes('tg.en.lgtest.fun');
    }

    public static isMirland(): boolean {
        let host = window.location.hostname;
        return host.includes('mirland.mobi');
    }

    public static isKongUser(): boolean {
        let host = window.location.hostname;
        return host.includes('kong.mirland.mobi');
    }

    public static isCrazyUser(): boolean {
        let host = window.location.hostname;
        return host.includes('crazy.mirland.mobi');
    }

    public static isPGUser(): boolean {
        let host = window.location.hostname;
        return host.includes('pg.zazer.mobi')
            || host.includes('pg.feature.lgtest.fun')
            || host.includes('pg.dev.lgtest.fun')
            || host.includes('pg.release.lgtest.fun')
            || host.includes('placeholder.games.playgama.com')
            || host.includes('playgama.com');
    }

    public static isFotoUser(): boolean {
        let host = window.location.hostname;
        return host.includes('foto.zazer.mobi');
    }

    /**
     * iOS / Android WebView (native bootstrap or Capacitor mobile build).
     * Used to enable app-only UX (e.g. language switch on feature/prod fronts).
     */
    public static isNativeMobileApp(): boolean {
        if (process.env.VUE_APP_IS_MOBILE_APP === 'true') {
            return true;
        }
        try {
            const platform = (window as any).__AOF_NATIVE_BOOTSTRAP__?.platform;
            if (platform === 'IOS' || platform === 'ANDROID' || platform === 'TEST') {
                return true;
            }
        } catch (e) {
            // ignore
        }
        try {
            const app = (window as any).app;
            if (app && (typeof app.isWebView === 'function' || app.isWebView === true)) {
                return true;
            }
        } catch (e) {
            // ignore
        }
        return false;
    }

    /**
     * Unified lang defaults + pass lang on register/Google/Apple.
     * NOW: native iOS/Android only. Web stays on legacy setLangDefaults.
     * FUTURE: enable for ya / gp / mystore / zazer.mobi / mirland.mobi.
     */
    public static isUnifiedLangFlowEnabled(): boolean {
        if (IntegrationUtils.isNativeMobileApp()) {
            return true;
        }
        // FUTURE:
        // if (IntegrationUtils.isYandexUser()) return true;
        // if (IntegrationUtils.isGPUser()) return true;
        // if (IntegrationUtils.isMystoreUser()) return true;
        // const host = window.location.hostname;
        // if (host.includes('zazer.mobi') || host.includes('mirland.mobi')) return true;
        return false;
    }

    /** RU shard hosts (for unified lang defaults in apps). */
    public static isRuShardHost(): boolean {
        const host = window.location.hostname;
        return host.includes('zazer.mobi')
            || host.includes('zazer.fun')
            || host.includes('lglass.mobi')
            || host.includes('glass.mobi');
    }

    /** FOREIGN shard hosts (for unified lang defaults in apps). */
    public static isForeignShardHost(): boolean {
        const host = window.location.hostname;
        return host.includes('mirland.mobi')
            || host.includes('lgtest.fun');
    }

    public static isI18NEnabled(): boolean {
        let host = window.location.hostname;
        if (host.includes('ya.zazer') || host.includes('localhost')
            || host.includes('mystore.zazer') || host.includes('foto.zazer') || host.includes('glass.mobi')) {
            return true;
        }
        // Mobile apps on feature / prod fronts (char select + profile lang button).
        if (IntegrationUtils.isNativeMobileApp()) {
            return host.includes('zazer.fun')
                || host.includes('lgtest.fun')
                || host.includes('zazer.mobi')
                || host.includes('mirland.mobi')
                || host.includes('lglass.mobi');
        }
        return false;
    }

    public static isExternalUser(): boolean {
        let host = window.location.hostname;
        return host.includes('mm.zazer') || host.includes('ok.zazer') || host.includes('ya.zazer') 
            || host.includes('tg.zazer') || host.includes('tg.release.lgtest') || host.includes('tg.mirland') || host.includes('tg.en.lgtest')
            || host.includes('vk.zazer') || host.includes('mystore.zazer') || host.includes('foto.zazer')
            || host.includes('otest.zazer') || host.includes('exe.zazer')
            || host.includes('exe.lgtest') || host.includes('beeline.zazer') || host.includes('beeline.lgtest')
            || host.includes('crazy.mirland') || host.includes('kong.mirland')
            || host.includes('gp.zazer') || host.includes('gp.feature.lgtest.fun') || host.includes('gp.dev.lgtest.fun') || host.includes('gp.release.lgtest.fun')
            || host.includes('pg.zazer') || host.includes('pg.feature.lgtest.fun') || host.includes('pg.dev.lgtest.fun') || host.includes('pg.release.lgtest.fun')
            || host.includes('playgama.com');
    }

    public static isPasswordResetEnabled(): boolean {
        return !this.isVkUser()
            && !this.isYandexUser()
            && !this.isMystoreUser()
            && !this.isFotoUser()
            && !this.isExeUser()
            && !this.isBeelineUser()
            && !this.isCrazyUser()
            && !this.isKongUser()
            && !this.isTelegramUser()
            && !this.isOkUser()
            && !window.location.hostname.includes('mm.zazer')
            && !window.location.hostname.includes('gamedistrib');
    }

    public static isSocialAuthAvailable(): boolean {
        if (IntegrationUtils.isRussianServer()) {
            return false;
        }

        let host = window.location.hostname;
        return !host.includes('feature.zazer.fun')
            && !host.includes('release.zazer.fun')
            && !host.includes('dev.lgtest.fun')
            && !host.includes('mirror.lgtest.fun')
            && !host.includes('feature.lgtest.fun')
            && !host.includes('release.lgtest.fun')
            && !host.includes('feature.k8s.zazer.fun')
            && !host.includes('release.k8s.zazer.fun');
    }

    public static importAuthSDKs(): void {
        if (!IntegrationUtils.isSocialAuthAvailable()) {
            return;
        }
        IntegrationUtils.addScriptOnce('https://accounts.google.com/gsi/client');
        IntegrationUtils.addScriptOnce('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
    }

    public static importPaymentSDK(player: Player) {
        let host = window.location.hostname;
        if (host.includes('ok.zazer.mobi') || host.includes('otest.zazer.mobi') || (!!player && player.userSource === UserSource.ok)) {
            IntegrationUtils.addScript('https://api.ok.ru/js/fapi5.js');
        }
    }

    public static initPaymentAPI(player: Player): void {
        let userSource: UserSource = player.userSource;
        if (userSource === UserSource.ok) {
            IntegrationUtils.initOkAPI();
        } else if (userSource === UserSource.vk) {
            IntegrationUtils.initVkPaymentAPI();
        } else if (IntegrationUtils.isYandexUser()) {
            // Checking old payments:
            try {
            // @ts-ignore
                window.ysdk.getPayments({signed: true}).then((payments: any) => {
                    payments.getPurchases().then((purchases: any) => purchases.forEach((purchase: any) => {
                        SecurityStore.buyYandex(purchase.productID, purchases.signature).then((gems: number) => {
                            payments.consumePurchase(purchase.purchaseToken);
                        });
                    })).catch((ignore: any) => {});
                    payments.getCatalog().then((products: any) => {
                        if (products && products.length > 0) {
                            for (let p of products) {
                                const yaProduct = IntegrationUtils.parseYandexProduct(p);
                                if (yaProduct) {
                                    GameState.YANDEX_PRODUCTS.set(p['id'], yaProduct);
                                }
                            }
                        }
                    }).catch((ignore: any) => {});
                }).catch((ignore: any) => {});
            } catch (ignore: any) {}
        } else if (IntegrationUtils.isFotoUser()) {
            var errorCallBack = function() {};
            // @ts-ignore
            Window.client = new fsapi("lglass","5dfd94814b92b3108e0a17a71eb4b6f1");
            // @ts-ignore
            Window.client.init(errorCallBack);
        } else if (IntegrationUtils.isGPUserSource(userSource) || IntegrationUtils.isGPUser()) {
            IntegrationUtils.initGPPaymentAPI();
        } else if (IntegrationUtils.isPGUser()) {
            const b = (window as any).bridge;
            if (b?.payments?.isSupported) {
                b.payments.getCatalog()?.then((items: any[]) => {
                    if (items && items.length > 0) {
                        for (let item of items) {
                            const priceStr = `${item.price}`.trim();
                            GameState.PG_PRODUCTS.set(item.id, priceStr);
                        }
                        console.log('Playgama products loaded:', GameState.PG_PRODUCTS.size);
                    }
                }).catch(() => {});
            }
        }
    }

    public static showOkPaymentDialog(price: number, name: string, desc: string, code: string): void {
        // @ts-ignore

        // name,
        // description,
        // code,
        // price,
        // options,
        // attributes,
        // currency,
        // callback,
        // uiConf
        if (typeof FAPI !== 'undefined' && FAPI) {
            // @ts-ignore
            window.API_callback = function (method, result, data) {
                if (result === 'ok') {
                    ListenerManager.callOkGemsCallback();
                } else {
                    console.log("Ok callback - result: " + result);
                }
            }
            // @ts-ignore
            FAPI.UI.showPayment(name, desc, code, price, null, null, "ok", "true");
        } else {
            console.error('OK Payment failed: FAPI is not defined.');
        }
    }

    public static showOkInviteDialog(text:string, refCode:string): void {
        // @ts-ignore

        // text
        // params
        // selected_uids
        if (typeof FAPI !== 'undefined' && FAPI) {
            // @ts-ignore
            FAPI.UI.showInvite(text, "ref=" + refCode);
        } else {
            console.error('OK invite failed: FAPI is not defined.');
        }
    }

    public static showFbShareWindow(player:Player, refCode: string):void{
        let refLink = this.getRefLink(player, refCode);
        window.open('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(refLink!), '_blank');
    }

    public static showYaPaymentDialog(productId: string, successCallback: (gems: number) => unknown): void {
        // @ts-ignore
        window.ysdk.getPayments({ signed: true }).then((payments: any) => {
            payments.purchase({ id: productId }).then((purchase: any) => {
                SecurityStore.buyYandex(productId, purchase.signature).then((gemsToTake: number) => {
                    payments.consumePurchase(purchase.purchaseToken);
                });
            }).catch((err: any) => {
                console.log('Yandex Payment ERROR ' + err);
            })
        }).catch((err: any) => {
            console.log('Yandex Payment ERROR ' + err);
        });
    }

    public static showTelegramPaymentDialog(productId: string): void {
        SecurityStore.openTelegramInvoice(productId).then((url) => {
            // @ts-ignore
            window.Telegram.WebApp.openInvoice(url.data, (status) => {
                if (status === 'paid') {
                    SecurityStore.buyTelegram(productId);
                }
            });
        });
    }

    public static showVkPaymentDialog(id: string): void {
        // @ts-ignore
        vkBridge.send("VKWebAppShowOrderBox", {type: 'item', item: id});
        // @ts-ignore
        vkBridge.subscribe((e: any) => {
            console.log(e);
        });
    }

    public static showVkInviteDialog(text: string, refCode:string): void {
        // @ts-ignore
        vkBridge.send('VKWebAppShowInviteBox', {requestKey: refCode}).then((data) => {
                // if (data.success) {
                // }
        // @ts-ignore
        }).catch((e) => {
            console.log(e);
        });
    }

    public static showMystorePaymentDialog(id: string, price: number, gemsCount: number, valuteName: string): void {
        if (valuteName !== 'USD' && valuteName !== 'EUR') {
            valuteName = 'RUB';
        }
        // @ts-ignore
        Window.mystoreApi.paymentFrame({
            'merchant_param': {
                'amount': price,
                'currency': valuteName,
                'description': String(gemsCount) + Tr('izumrudov'),
                'item_id': id
            }
        });
    }

    public static showKongregatePaymentDialog(bo: BankOption): void {
        // @ts-ignore
        window.kongregate.mtx.purchaseItemsRemote(bo.id, function(result){{
            // TODO
        }});
    }

    public static showFotoPaymentDialog(bo: BankOption): void {
        // @ts-ignore
        Window.client.event("buyItemCallback", (obj: any) => {
            console.log('Client FS API response');
            console.log(obj);
        }, {
            picUrl: 'https://foto.zazer.mobi/assets/shop/' + bo.imageId + '.png',
            name: bo.title,
            amount: 1,
            itemId: bo.id,
            priceFmCents: bo.price * 100
        });
    }

    public static initOkAPI() {
        // @ts-ignore
        if (typeof FAPI !== 'undefined' && FAPI) {
            // @ts-ignore
            let rParams = FAPI.Util.getRequestParameters();
            // @ts-ignore
            FAPI.init(rParams["api_server"], rParams["apiconnection"],
                function () {
                    console.log(Tr('initsializatsija.proshla.uspeshno'));
                }, function (error: any) {
                    console.log(Tr('oshibka.initsializatsii') + error);
                });

            // @ts-ignore
            window.API_callback = function (method, result, data) {
                if (result === 'ok') {
                    ListenerManager.callOkGemsCallback();
                } else {
                    console.log("Ok callback - result: " + result);
                }
            }
        } else {
            console.error('Failed to init FAPI - OK Payment API');
        }
    }

    private static initVkPaymentAPI() {
        // @ts-ignore
        if (typeof vkBridge !== 'undefined' && vkBridge) {
            // @ts-ignore
            vkBridge.send('VKWebAppInit', {});
        }
    }

    private static addScript(scriptSource: string) {
        var s = document.createElement( 'script' );
        s.setAttribute('src', scriptSource);
        document.body.appendChild(s);
    }

    private static addScriptOnce(scriptSource: string) {
        if (document.querySelector('script[src="' + scriptSource + '"]')) {
            return;
        }
        var s = document.createElement('script');
        s.setAttribute('src', scriptSource);
        s.async = true;
        s.defer = true;
        document.body.appendChild(s);
    }

    public static registerMystoreUser(mystoreRegisterCallback: (uid: string, hash: string) => unknown) {
        IntegrationUtils.mystoreRegisterCallback = mystoreRegisterCallback;
        // @ts-ignore
        Window.mystoreApi.registerUser();
    }

    public static initMystore() {
        (function apiHandshake(iframeApi) {
            if (typeof iframeApi === 'undefined') {
                console.log('Cannot find iframeApi function, are we inside an iframe?');
                return;
            }

            var externalApi = null;

            var callbacks = {
                appid: 18842,

                getLoginStatusCallback: function(status: any) {},
                userInfoCallback: function(info: any) {
                    console.log('=== userInfoCallback ===');
                    console.log(info);
                    if (IntegrationUtils.mystoreRegisterCallback) {
                        Http.MYSTORE_UID = info.uid;
                        IntegrationUtils.mystoreRegisterCallback(info.uid, info.hash);
                    }
                },
                userProfileCallback: function(profile: any) {},
                registerUserCallback: function(info: any) {
                    if (info.status === 'error' && info.errcode === 1001) {
                        console.log('User already registered');
                        // @ts-ignore
                        Window.mystoreApi.userInfo();
                    } else if (info.status === 'ok') {
                        console.log('=== registerUserCallback ===');
                        console.log(info);
                        if (IntegrationUtils.mystoreRegisterCallback) {
                            Http.MYSTORE_UID = info.uid;
                            IntegrationUtils.mystoreRegisterCallback(info.uid, info.hash);
                        }
                    } else {
                        console.log('Status is not OK');
                        console.log(info);
                        // @ts-ignore
                        Window.mystoreApi.authUser();
                    }
                },
                paymentFrameUrlCallback: function(url: any) {},
                getAuthTokenCallback: function(token: any) {},
                paymentReceivedCallback: function(data: any) {},
                paymentWindowClosedCallback: function() {},
                userConfirmCallback: function() {},
                paymentFrameItem: function(args: any) {},
                getGameInventoryItems: function() {}
            };

            function error(err: any) {
                throw new Error('Could not init external api ' + err);
            }

            function connected(api: any) {
                externalApi = api;
                // @ts-ignore
                Window.mystoreApi = api;
            }

            iframeApi(callbacks).then(connected, error);
            // @ts-ignore
        }(window.iframeApi));
    }

    public static showOkPostDialog(successCallback: () => unknown): void {
        // @ts-ignore
        if (typeof FAPI !== 'undefined' && FAPI) {
            // @ts-ignore
            window.API_callback = function (method, result, data) {
                if (result === 'ok') {
                    successCallback();
                } else {
                    console.log(result);
                    console.log(data);
                }
            }
            let attachment = {
                "media":[
                    {
                        "type": "text",
                        "text": Tr('intro')
                    },
                    {
                        "type": "app",
                        "images": [
                            {
                                "url": "https://ok.zazer.mobi/assets/banner.jpg",
                                "title": Tr('game.name')
                            }
                        ]
                    }
                ]
            };
            // @ts-ignore
            FAPI.UI.postMediatopic(attachment, false);
        } else {
            console.error('OK Payment failed: FAPI is not defined.');
        }
    }

    public static initExeAPI() {
        // @ts-ignore
        window['handleExeResponse'] = function(functionName: any, result: any) {
            console.log(functionName);
            console.log(result);
        }

        var options = {
            'friends.getAppUsers': 'handleExeResponse',
            'showRequestBox': 'handleExeResponse',
            'showOrderBox': 'handleExeResponse',
            'users.get': 'handleExeResponse'
        };

        // @ts-ignore
        window.exeApi = new ExeRuApi(options);
        // @ts-ignore
        window.exeApi.setHeight(CU.CANVAS_HEIGHT);
    }

    public static initGPPaymentAPI() {
        const gp = (window as any).gp;
        if (gp) {
            if (gp.payments && gp.payments.products && gp.payments.products.length > 0) {
                for (let product of gp.payments.products) {
                    if (product.tag) {
                        let priceStr = `${product.price} ${product.currencySymbol || ''}`.trim();
                        GameState.GP_PRODUCTS.set(product.tag, priceStr);
                    }
                }
                console.log('GamePush products loaded:', GameState.GP_PRODUCTS.size);
            }
        }
    }

    public static showExePaymentDialog(id: string): void {
        // @ts-ignore
        // window.exeApi.users.get({
        //     "fields": "sex,bdate,photo_50"
        // });

        // @ts-ignore
        window.exeApi.showOrderBox({
            "type": "item",
            "item": id,
        });
    }

    public static initRustorePaymentApi(): void {
        if (!!(window as any).app && typeof (window as any).app.initPaymentApi === 'function') {
            (window as any).app.initPaymentApi(PlayerStore.getPlayer().id);
        }
    }

    public static initPlaymarketPaymentApi(): void {
        if (!!(window as any).app && typeof (window as any).app.initPaymentApi === 'function') {
            (window as any).app.initPaymentApi(PlayerStore.getPlayer().id);
        }
    }

    public static showRustorePaymentDialog(productId: string): void {
            (window as any).app.initiatePayment(productId, PlayerStore.getPlayer().id);
    }

    public static isClipboardAPIEnabled(): boolean {
        return true;
        // return !this.isYandexUser();
    }

    public static isReferralSystemEnabled() {
        //google, beeline - реализуем позже
        //mail, gamedistrib - неактуальные платформы

        return this.isYandexUser() || this.isVkUser() || this.isOkUser() || this.isExeUser() || this.isCrazyUser() ||
            this.isFotoUser() || this.isMystoreUser() || this.isKongUser() || this.isGPUser() || !this.isExternalUser();
    }

    public static showGamePushPaymentDialog(tag: string): void {
        const gp = (window as any).gp;
        
        if (!gp) {
            console.error('GamePush не инициализирован');
            return;
        }

        if (gp.payments.isAvailable) {
            gp.payments.purchase({ tag: tag })
                .then((result: any) => {
                    console.log('GamePush payment successful:', result);
                    ListenerManager.callPurchaseRecievedCallbacl();
                })
                .catch((error: any) => {
                    console.error('GamePush payment error:', error);
                });
        } else {
            console.warn('GamePush payments не доступны');
        }
    }

    public static showPlaygamaPaymentDialog(productId: string): void {
        const b = (window as any).bridge;
        if (!b?.payments?.isSupported) {
            console.warn('Playgama payments not supported');
            return;
        }
        b.payments.purchase(productId)
            .then((purchase: any) => {
                console.log('PG payment successful:', purchase);
                const orderId = purchase?.orderId || purchase?.jwt?.orderId || undefined;
                const externalId = purchase?.externalId || purchase?.jwt?.externalId || undefined;
                SecurityStore.buyPlaygama(productId, orderId, externalId).then(async () => {
                    try {
                        if (typeof b.payments.consumePurchase === 'function') {
                            await b.payments.consumePurchase(productId);
                        }
                    } catch (e) {
                        console.error('PG consume error:', e);
                    }
                    ListenerManager.callPurchaseRecievedCallbacl();
                });
            })
            .catch((error: any) => {
                console.error('PG payment error:', error);
            });
    }

    public static getRefLink(player:Player, refCode:string): string | null {
        switch(player.userSource){
            case UserSource.own:
                if (window.location.hostname.includes("mirland.mobi")) {
                    return 'https://mirland.mobi?ref='+refCode;
                } else if (!window.location.hostname.includes("zazer.mobi")) {
                    let parentUrl = (window.location != window.parent.location)
                        ? document.referrer
                        : document.location.href;
                    let parentHost = parentUrl.indexOf('?') != -1? parentUrl.split('?')[0] : parentUrl;
                    return parentHost + '?ref=' + refCode;
                }
                return 'https://zazer.mobi?ref='+refCode;
            case UserSource.vk:
                return 'https://vk.com/app8110149?request_key=' + refCode;
            case UserSource.ok:
                return 'https://ok.ru/game/lglass?ref=' + refCode;
            case UserSource.kong:
                return 'https://www.kongregate.com/games/hitmakers/mirrorland?ref=' + refCode;
            case UserSource.foto:
                return 'https://fotostrana.ru/app/lglass/?ref=' + refCode;
            case UserSource.mystore:
                return 'https://vkplay.ru/app/18842?ref=' + refCode;
            case UserSource.exe:
                return 'https://exe.ru/app669?request_key=' + refCode;

            case UserSource.crazygames:
            case UserSource.ya:
                let parentUrl = (window.location != window.parent.location)
                    ? document.referrer
                    : document.location.href;
                let parentHost = parentUrl.indexOf('?') != -1? parentUrl.split('?')[0] : parentUrl;
                return parentHost + '?ref=' + refCode;

            //реализуем позже
            case UserSource.google:
            case UserSource.beeline:
            case UserSource.gp:
            case UserSource.gp_partner:
            case UserSource.gp_playdeck:
            //неактуальные платформы
            case UserSource.gamedistrib:
            case UserSource.mail:
        }

        return null;
    }

    public static getRefCode(): string | null {
        if (!this.isReferralSystemEnabled()){
            return  null;
        }

        if(this.isOkUser()){
            let customArgs = this.getUrlParameter("custom_args");
            return this.getParameterFromEncodedQueryString(customArgs, "ref")
        }

        if(this.isVkUser()){
            // let hash = this.getUrlParameter("hash");
            // return this.getParameterFromEncodedQueryString(hash, "ref")
            return this.getUrlParameter("request_key")
        }

        if(this.isExeUser()){
            return this.getUrlParameter("request_key");
        }

        if(this.isKongUser()){
            this.getParentUrlParameter("ref");
        }

        return this.getUrlParameter("ref");
    }

    public static getUrlParameter(key: string): string | null {
        try {
            return this.doGetUrlParameter(window.location.search, key);
        } catch (e){
            console.error("Error getting url argument " + key)
            return null;
        }
    }

    public static getParentUrlParameter(key: string):string | null{
        try {
            let url = (window.location != window.parent.location) ? document.referrer : document.location.href;
            return this.doGetUrlParameter(url, key);
        } catch (e){
            console.error("Error getting url argument " + key)
            return null;
        }
    }

    private static doGetUrlParameter(url:string, key:string): string | null{
            key = key.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
            const regex = new RegExp('[\\?&]' + key + '=([^&#]*)');
            const results = regex.exec(url);
            return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    public static getParameterFromEncodedQueryString(encodedParameters:string|null, key: string): string | null {
        try {
            if(!encodedParameters){
                return null;
            }
            let parametersString = decodeURIComponent(encodedParameters);
            let parts = parametersString.split('&');
            for (let part in parts){
                if (part.split("=")[0] == key){
                    return part.split("=")[1];
                }
            }
            return null;
        } catch (e){
            console.error("Error getting parameter from encoded query string:  " + key)
            return null;
        }
    }

    // public static oldCopyToClipboard(text:string) {
    //     navigator.clipboard.writeText(text);
    // }

    public static copyToClipboard(text:string) {
        // Создаем временный элемент для копирования
        const tempInput = document.createElement('textarea');
        tempInput.value = text;
        document.body.appendChild(tempInput);

        // Выделяем текст в элементе
        tempInput.select();
        tempInput.setSelectionRange(0, 99999); // Для мобильных устройств

        // Копируем текст в буфер обмена
        document.execCommand('copy');

        // Удаляем временный элемент
        document.body.removeChild(tempInput);
    }

    public static isRustoreUser(): boolean {
        let host = window.location.hostname;
        // TODO uncomment before release
        if (!host.includes('apk.lglass.mobi')) {
            return false;
        }
        const ua: string = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
        const isWv: boolean = /; wv\)/.test(ua);
        const hasAndroidInterface: boolean = typeof (window as any).app !== 'undefined' &&
                                            typeof (window as any).app.isWebView !== 'undefined';

        return isWv && hasAndroidInterface;
    }

    public static isPlayMarketUser(): boolean {
        let host = window.location.hostname;
        // todo change to production urls
        // https://play.lglass.mobi/
        // https://play.mirland.mobi/
        if (!host.includes('feature.lgtest.fun')) {
            return false;
        }
        const ua: string = navigator.userAgent || '';
        const isWv: boolean = /; wv\)/.test(ua);
        const hasAndroidInterface: boolean = typeof (window as any).app !== 'undefined' &&
            typeof (window as any).app.isWebView !== 'undefined';
        return isWv && hasAndroidInterface;
    }

    public static getRustoreAppVersion(): integer {
        const ua: string = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
        const isWv: boolean = /; wv\)/.test(ua);
        const hasAndroidInterface: boolean = typeof (window as any).app !== 'undefined' &&
            typeof (window as any).app.getAppVersion !== 'undefined';
        if (isWv && hasAndroidInterface)
            return (window as any).app.getAppVersion();
        else
            return 0;
    }

    public static showLogInMobileApp(logMsg: string): void {
        (window as any).app.addLog(logMsg);
    }

    public static showPlaymarketPaymentDialog(productId: string,
                                        price: number,
                                        title: string,
                                        isUseXsolla: Boolean): void {
        (window as any).app.initiatePayment(productId, PlayerStore.getPlayer().id);
    }

    public static showPlaymarketRateWindow(): void {
        (window as any).app.initRateApp();
    }

    public static showPlaymarketRegisterPage(isNewPlayer: boolean): void {
        const player = PlayerStore.getPlayer();
        const lang = player?.lang ?? '';
        const id = player?.id ?? 0;
        const uniqueId = player?.uniqueId ?? '';

        (window as any).app.signInWithGoogle(lang, id, uniqueId, isNewPlayer);
    }

    public static showRustoreRateWindow(): void {
        (window as any).app.initRateApp();
    }

    public static isGPUserSource(userSource: UserSource): boolean {
        return userSource === UserSource.gp || 
               userSource === UserSource.gp_partner ||
               userSource === UserSource.gp_playdeck;
    }

    public static isIosUser(): boolean {
        let host = window.location.hostname;
        // TODO uncomment before release
        if (!host.includes('apk.lglass.mobi')) {
            return false;
        }
        const ua: string = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
        const isIos: boolean = /iPad|iPhone/i.test(ua);
        const hasIosInterface: boolean =
            typeof (window as any).webkit !== 'undefined' &&
            typeof (window as any).webkit.messageHandlers !== 'undefined';

        return isIos && hasIosInterface;
    }

    private static parseYandexProduct(product: any): YandexProduct | undefined {
        if (!product?.id) {
            return undefined;
        }

        const priceValue = product.priceValue != null ? String(product.priceValue) : '';
        const priceCurrencyCode = product.priceCurrencyCode != null ? String(product.priceCurrencyCode) : '';
        let currencyImageUrl: string | undefined;

        if (typeof product.getPriceCurrencyImage === 'function') {
            try {
                currencyImageUrl = product.getPriceCurrencyImage('small')
                    || product.getPriceCurrencyImage('medium')
                    || product.getPriceCurrencyImage('svg');
            } catch (ignore: any) {}
        }

        if (!priceValue && !priceCurrencyCode) {
            return undefined;
        }

        return {priceValue, priceCurrencyCode, currencyImageUrl};
    }

    public static isIosApp(): boolean {
        let w = window as any;
        return !!(w.webkit && w.webkit.messageHandlers && w.webkit.messageHandlers.appleSignIn);
    }

    /**
     * Taptic Engine via native iOS bridge. No-op outside the iOS app.
     * Styles: light | medium | heavy | soft | rigid | selection | success | warning | error
     */
    public static haptic(style: string = 'medium'): void {
        let w = window as any;
        let handler = w.webkit && w.webkit.messageHandlers && w.webkit.messageHandlers.haptic;
        if (!handler) {
            return;
        }
        try {
            handler.postMessage({style});
        } catch (e) {
            // ignore bridge failures (web / unsupported)
        }
    }

    public static appleSignIn(): Promise<AppleAuthResult> {
        return IntegrationUtils.isIosApp()
            ? IntegrationUtils.nativeAppleSignIn()
            : IntegrationUtils.webAppleSignIn();
    }

    private static nativeAppleSignIn(): Promise<AppleAuthResult> {
        return new Promise<AppleAuthResult>((resolve, reject) => {
            let w = window as any;

            w.onAppleSignInResult = (raw: any) => {
                delete w.onAppleSignInResult;

                let res: any;
                try {
                    res = typeof raw === 'string' ? JSON.parse(raw) : raw;
                } catch (e) {
                    reject(new Error('Некорректный ответ от приложения'));
                    return;
                }

                if (!res || res.error) {
                    reject(new Error((res && res.error) || 'Ошибка входа через Apple'));
                    return;
                }

                let claims = IntegrationUtils.parseJwt(res.idToken);
                let email = res.email || claims.email || '';
                let fullName = ((res.firstName || '') + ' ' + (res.lastName || '')).trim();
                if (!fullName) {
                    fullName = email ? email.split('@')[0] : '';
                }

                resolve({idToken: res.idToken, fullName, email});
            };

            w.webkit.messageHandlers.appleSignIn.postMessage({});
        });
    }

    private static async webAppleSignIn(): Promise<AppleAuthResult> {
        let w = window as any;
        if (!w.AppleID) {
            throw new Error('Apple SDK не загружен. Попробуйте позже.');
        }

        w.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: 'name email',
            redirectURI: window.location.origin,
            usePopup: true
        });

        let response = await w.AppleID.auth.signIn();
        let idToken = response.authorization.id_token;
        let claims = IntegrationUtils.parseJwt(idToken);

        let email = (response.user && response.user.email) || claims.email || '';
        let fullName = '';
        if (response.user && response.user.name) {
            fullName = ((response.user.name.firstName || '') + ' ' + (response.user.name.lastName || '')).trim();
        }
        if (!fullName) {
            fullName = email ? email.split('@')[0] : '';
        }

        return {idToken, fullName, email};
    }

    private static parseJwt(token: string): any {
        try {
            let base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            let json = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            return JSON.parse(json);
        } catch (e) {
            return {};
        }
    }
}
